import {
  Bell,
  Mail,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Users,
  ArrowRight,
  ChevronLeft,
  List,
  MailOpen,
  PartyPopper,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { notifications as toastNotifications } from '../../../config/toast-notifications.js';
import type { CombinedNotification } from '../../hooks/useNotifications.js';
import { useNotifications } from '../../hooks/useNotifications.js';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog.js';

/**
 * CatchUpCardStack — a Slack-style focused "Catch Up" walk-the-stack view for
 * notifications on the web (desktop) experience.
 *
 * Shows ONE near-full-screen card at a time for each unread notification /
 * mention drawn from {@link useNotifications} (the single source of truth). The
 * user walks the stack — open the deep-link, mark read, keep unread, or advance
 * — until every item is handled, ending on a friendly "all caught up" state.
 *
 * This component owns ONLY presentation + local walk state (current index).
 * ALL data + mutations come from the shared hook, exactly like NotificationBadge,
 * so the bell dropdown and this view never drift.
 *
 * Mobile is a later phase; the card is already the atomic unit so a swipeable
 * full-screen adaptation can reuse it without reworking the data flow.
 */

interface CatchUpCardStackProps {
  currentUserId: string;
  /** Whether the focused stack is open. */
  open: boolean;
  /** Called when the stack should close (Esc, backdrop, close button, or done). */
  onClose: () => void;
  /** Called to switch to the traditional dropdown list view. */
  onViewList?: () => void;
}

// Stable identity for a stream item so the walk index survives refetches that
// re-create the array. Mentions and notifications can't collide (id namespaces
// are per-table but we prefix anyway).
function itemKey(item: CombinedNotification): string {
  return item.type === 'notification'
    ? `notification-${item.data.id}`
    : `mention-${item.data.id}`;
}

function formatTimestamp(timestamp: string | null | undefined): string {
  if (!timestamp) {
    return 'Unknown time';
  }
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return date.toLocaleString();
}

// Strip the @[Display Name](UUID) mention markup down to @Display Name.
function cleanMentionContent(content: string | null | undefined): string {
  if (!content) {
    return 'No content';
  }
  return content.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
}

function getActivityNotificationIcon(
  notificationTypeCode: string | null | undefined
): React.ReactNode {
  switch (notificationTypeCode) {
    case 'ACTIVITY_INSTALL_DATE_CHANGED_72H':
      return <AlertTriangle className="w-6 h-6 text-orange-500" />;
    case 'ACTIVITY_INSTALL_DATE_CHANGED':
      return <Calendar className="w-6 h-6 text-blue-500" />;
    case 'ACTIVITY_STATUS_CHANGED':
      return <CheckCircle className="w-6 h-6 text-green-500" />;
    case 'ACTIVITY_ASSIGNED':
      return <Users className="w-6 h-6 text-purple-500" />;
    case 'ACTIVITY_COMPLETED':
      return <CheckCircle className="w-6 h-6 text-emerald-500" />;
    default:
      return <Bell className="w-6 h-6 text-gray-500" />;
  }
}

function getImportanceBadgeVariant(
  importance: string | null | undefined
): 'destructive' | 'warning' | 'secondary' {
  switch (importance) {
    case 'urgent':
      return 'destructive';
    case 'high':
      return 'warning';
    default:
      return 'secondary';
  }
}

const CatchUpCardStack: React.FC<CatchUpCardStackProps> = ({
  currentUserId,
  open,
  onClose,
  onViewList,
}) => {
  const navigate = useNavigate();

  const {
    items,
    unreadCount,
    refetch,
    isMutating,
    getMentionContext,
    resolveNotificationUrl,
    markNotificationRead,
    markNotificationUnread,
    markMentionRead,
    markMentionUnread,
  } = useNotifications(currentUserId);

  // Local walk state. We freeze the stream into a stable ordered snapshot while
  // the stack is open so marking an item read (which drops it from the live
  // `items`) does not yank cards out from under the user. Cards the user has
  // acted on are shown as "handled" via the per-item read state we track here.
  const [snapshot, setSnapshot] = useState<CombinedNotification[]>([]);
  const [index, setIndex] = useState(0);
  // Keys the user explicitly marked read in this session (so we can badge them).
  const [readKeys, setReadKeys] = useState<Set<string>>(new Set());

  // (Re)seed the snapshot when the stack opens. Refetch first so we start fresh.
  useEffect(() => {
    if (open) {
      refetch();
      setSnapshot(items);
      setIndex(0);
      setReadKeys(new Set());
    }
    // Intentionally only re-seed on `open` toggling true — not on every `items`
    // change — so the walk order is stable for the duration of the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If the snapshot was empty at open time but items arrive slightly later
  // (first fetch still in flight), adopt them once.
  useEffect(() => {
    if (open && snapshot.length === 0 && items.length > 0) {
      setSnapshot(items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, items]);

  const total = snapshot.length;
  const current = snapshot[index];
  const atEnd = index >= total;

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(i + 1, total));
  }, [total]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  // Resolve display fields + deep-link for the current card.
  const currentView = useMemo(() => {
    if (!current) {
      return null;
    }
    if (current.type === 'notification') {
      const n = current.data;
      return {
        key: itemKey(current),
        icon: getActivityNotificationIcon(n.notification_type_code),
        kindLabel: 'Event',
        who: n.title,
        contextLabel: null as string | null,
        contextSubtitle: null as string | null,
        body: n.message,
        importance: n.importance,
        timestamp: n.created_at,
        url: resolveNotificationUrl(n),
        actionText: n.action_text || 'Open',
      };
    }
    const m = current.data;
    const context = getMentionContext(m);
    return {
      key: itemKey(current),
      icon: (
        <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
          {m.trig_user_full_name
            ? m.trig_user_full_name
                .split(' ')
                .map((part) => part[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)
            : '??'}
        </div>
      ),
      kindLabel: m.mention_type || 'Mention',
      who: m.trig_user_full_name || 'Someone',
      contextLabel: context?.label ?? null,
      contextSubtitle: context?.subtitle ?? null,
      body: m.trig_note_title
        ? `${m.trig_note_title}\n${cleanMentionContent(m.trig_note_content)}`
        : cleanMentionContent(m.trig_note_content),
      importance: null as string | null,
      timestamp: m.mentioned_at,
      url: context?.url ?? null,
      actionText: 'Open',
    };
  }, [current, getMentionContext, resolveNotificationUrl]);

  // Mark the current item read (no navigation) and advance.
  const handleMarkReadAndNext = useCallback(async () => {
    if (!current) {
      return;
    }
    const key = itemKey(current);
    try {
      if (current.type === 'notification') {
        await markNotificationRead(current.data);
      } else {
        await markMentionRead(current.data);
      }
      setReadKeys((prev) => new Set(prev).add(key));
    } catch (error) {
      console.error('[CatchUp] Failed to mark read:', error);
      toastNotifications.error('Error', 'Failed to mark as read');
    }
    goNext();
  }, [current, markNotificationRead, markMentionRead, goNext]);

  // Explicitly keep the current item unread and advance. If the user had marked
  // it read earlier in this session, restore it to unread.
  const handleKeepUnreadAndNext = useCallback(async () => {
    if (!current) {
      goNext();
      return;
    }
    const key = itemKey(current);
    if (readKeys.has(key)) {
      try {
        if (current.type === 'notification') {
          await markNotificationUnread(current.data);
        } else {
          await markMentionUnread(current.data);
        }
        setReadKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      } catch (error) {
        console.error('[CatchUp] Failed to mark unread:', error);
        toastNotifications.error('Error', 'Failed to mark as unread');
      }
    }
    goNext();
  }, [current, readKeys, markNotificationUnread, markMentionUnread, goNext]);

  // Open the deep-link: mark read, navigate, and close the stack.
  const handleOpen = useCallback(async () => {
    if (!current || !currentView) {
      return;
    }
    try {
      if (current.type === 'notification') {
        await markNotificationRead(current.data);
      } else {
        await markMentionRead(current.data);
      }
    } catch (error) {
      console.error('[CatchUp] Failed to mark read on open:', error);
    }
    if (currentView.url) {
      navigate(currentView.url);
      onClose();
    } else {
      toastNotifications.error(
        'No destination',
        'This notification has no linked location.'
      );
    }
  }, [current, currentView, markNotificationRead, markMentionRead, navigate, onClose]);

  // Keyboard navigation: ←/→ prev/next, Enter open, R mark read, U keep unread,
  // Esc close. Dialog handles Esc too but we keep it explicit for clarity.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Enter') {
        if (!atEnd) {
          e.preventDefault();
          void handleOpen();
        }
      } else if (e.key.toLowerCase() === 'r') {
        if (!atEnd) {
          e.preventDefault();
          void handleMarkReadAndNext();
        }
      } else if (e.key.toLowerCase() === 'u') {
        if (!atEnd) {
          e.preventDefault();
          void handleKeepUnreadAndNext();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [
    open,
    atEnd,
    goNext,
    goPrev,
    handleOpen,
    handleMarkReadAndNext,
    handleKeepUnreadAndNext,
  ]);

  const isCurrentRead = current ? readKeys.has(itemKey(current)) : false;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={cn(
          'max-w-3xl w-[92vw] h-[88vh] p-0 gap-0 flex flex-col',
          'sm:rounded-xl overflow-hidden'
        )}
      >
        {/* Header: title + progress */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <DialogTitle className="text-lg font-semibold">Catch Up</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {total > 0 && !atEnd
                ? `${index + 1} of ${total}`
                : `${unreadCount} unread`}
            </DialogDescription>
          </div>
          <div className="flex items-center gap-3">
            {total > 0 && !atEnd && (
              <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground">
                <kbd className="px-1.5 py-0.5 rounded border bg-muted">Enter</kbd>
                <span>open</span>
                <kbd className="px-1.5 py-0.5 rounded border bg-muted">R</kbd>
                <span>read</span>
                <kbd className="px-1.5 py-0.5 rounded border bg-muted">U</kbd>
                <span>keep</span>
                <kbd className="px-1.5 py-0.5 rounded border bg-muted">←/→</kbd>
                <span>move</span>
              </div>
            )}
            {onViewList && (
              <button
                type="button"
                onClick={onViewList}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium',
                  'text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                )}
                title="Switch to the traditional list view"
              >
                <List className="w-3.5 h-3.5" />
                View as list
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {total > 0 && (
          <div className="h-1 w-full bg-muted flex-shrink-0">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{
                width: `${Math.min((index / total) * 100, 100)}%`,
              }}
            />
          </div>
        )}

        {/* Body: two large full-height triage buttons flanking the content —
            LEFT = Keep Unread, RIGHT = Mark as Read (Slack-style). */}
        <div className="flex-1 min-h-0 flex items-stretch overflow-hidden">
          {atEnd || !currentView ? (
            // All caught up / empty state (spans full width)
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
                  <PartyPopper className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">
                  You&apos;re all caught up!
                </h2>
                <p className="text-muted-foreground mb-6">
                  {total === 0
                    ? 'You have no unread notifications right now.'
                    : `You've walked through all ${total} notification${total !== 1 ? 's' : ''}.`}
                </p>
                <Button onClick={onClose} size="lg">
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* LEFT — Keep Unread (full-height button) */}
              <button
                type="button"
                onClick={handleKeepUnreadAndNext}
                disabled={isMutating}
                aria-label="Keep unread and go to next"
                className={cn(
                  'group relative flex w-24 sm:w-40 flex-shrink-0 flex-col items-center justify-center gap-3',
                  'border-r border-border bg-muted/40 text-muted-foreground',
                  'transition-colors hover:bg-amber-100 hover:text-amber-900 active:bg-amber-200',
                  'dark:hover:bg-amber-900/30 dark:hover:text-amber-200',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                  'disabled:opacity-50 disabled:pointer-events-none'
                )}
              >
                <Mail className="w-7 h-7 sm:w-9 sm:h-9" />
                <span className="text-sm sm:text-base font-semibold leading-tight text-center px-2">
                  Keep<br />Unread
                </span>
                <span className="absolute bottom-3 hidden sm:block text-[10px] uppercase tracking-wide opacity-60">
                  press U
                </span>
              </button>

              {/* CENTER — the notification content (unchanged width) */}
              <div className="flex-1 min-w-0 overflow-y-auto flex items-center justify-center p-6 sm:p-8">
                <div className="w-full max-w-xl">
                  <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        {currentView.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant="outline" className="text-xs">
                            {currentView.kindLabel}
                          </Badge>
                          {currentView.importance &&
                            currentView.importance !== 'normal' && (
                              <Badge
                                variant={
                                  getImportanceBadgeVariant(
                                    currentView.importance
                                  ) as any
                                }
                                className="text-xs"
                              >
                                {currentView.importance}
                              </Badge>
                            )}
                          {currentView.contextLabel && (
                            <Badge variant="secondary" className="text-xs">
                              {currentView.contextLabel}
                            </Badge>
                          )}
                          {isCurrentRead && (
                            <Badge variant="success" className="text-xs">
                              Marked read
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-xl font-semibold text-foreground break-words">
                          {currentView.who}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {formatTimestamp(currentView.timestamp)}
                          {currentView.contextSubtitle
                            ? ` · ${currentView.contextSubtitle}`
                            : ''}
                        </p>
                      </div>
                    </div>

                    <p className="mt-6 text-base text-foreground/90 whitespace-pre-line break-words">
                      {currentView.body}
                    </p>

                    <div className="mt-8">
                      <Button
                        onClick={handleOpen}
                        size="lg"
                        className="w-full"
                        disabled={!currentView.url || isMutating}
                        title={
                          currentView.url
                            ? undefined
                            : 'This notification has no linked location'
                        }
                      >
                        {currentView.actionText}
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                      {!currentView.url && (
                        <p className="text-xs text-muted-foreground text-center mt-2">
                          No linked location for this notification.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT — Mark as Read (full-height button) */}
              <button
                type="button"
                onClick={handleMarkReadAndNext}
                disabled={isMutating}
                aria-label="Mark as read and go to next"
                className={cn(
                  'group relative flex w-24 sm:w-40 flex-shrink-0 flex-col items-center justify-center gap-3',
                  'border-l border-border bg-muted/40 text-muted-foreground',
                  'transition-colors hover:bg-emerald-100 hover:text-emerald-900 active:bg-emerald-200',
                  'dark:hover:bg-emerald-900/30 dark:hover:text-emerald-200',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                  'disabled:opacity-50 disabled:pointer-events-none'
                )}
              >
                <MailOpen className="w-7 h-7 sm:w-9 sm:h-9" />
                <span className="text-sm sm:text-base font-semibold leading-tight text-center px-2">
                  Mark as<br />Read
                </span>
                <span className="absolute bottom-3 hidden sm:block text-[10px] uppercase tracking-wide opacity-60">
                  press R
                </span>
              </button>
            </>
          )}
        </div>

        {/* Footer — Back only; Keep Unread / Mark as Read are the big side buttons */}
        {!atEnd && currentView && (
          <div className="flex items-center justify-between gap-2 px-6 py-3 border-t border-border flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={goPrev}
              disabled={index === 0}
              aria-label="Previous notification"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            <p className="hidden sm:block text-xs text-muted-foreground">
              ← Keep unread &nbsp;·&nbsp; Mark as read →
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CatchUpCardStack;
